// ① ホーム — 今月の進捗（デザイン正本 home.jsx を React/TS 化）
// 旧 Dashboard（粗利KPI）を廃止し、進捗中心に刷新。データは store から導出する。

import React, { useEffect, useMemo, useState } from "react";
import { Ic } from "../components/Icons";
import { useStore } from "../store";
import { yen } from "../lib/normalize";
import { fetchApprovalStatus } from "../lib/approvalApi";
import { isRetiredDriver } from "../lib/driverStatus";

type StepState = "done" | "current" | "todo";
type Step = { key: string; label: string; icon: keyof typeof Ic; state: StepState; meta: string };

function formatMonth(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split("-").map(Number);
  return `${y}年${m}月`;
}

// 進捗ステップ（大きく・横並び）
function ProgressStep({ s, last }: { s: Step; last: boolean }) {
  const done = s.state === "done";
  const cur = s.state === "current";
  const ring = done ? "var(--green)" : cur ? "var(--blue)" : "var(--border-strong)";
  const bg = done ? "var(--green)" : cur ? "var(--blue)" : "#fff";
  const fg = done || cur ? "#fff" : "var(--ink-4)";
  const Icon = done ? Ic.check : Ic[s.icon];
  return (
    <div className="home-step" style={{ flex: 1, display: "flex", alignItems: "flex-start", minWidth: 0 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div
          style={{
            width: "var(--step-icon, 52px)", height: "var(--step-icon, 52px)", borderRadius: 15, background: bg, border: "1.5px solid " + ring,
            color: fg, display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: cur ? "0 6px 16px rgba(37,99,235,.3)" : "none",
          }}
        >
          <Icon size={24} strokeWidth={done ? 2.4 : 1.8} />
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div className="home-step-text" style={{ padding: "0 14px", minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", color: done ? "var(--green)" : cur ? "var(--blue-dark)" : "var(--ink-4)" }}>
              {done ? "完了" : cur ? "進行中" : "未着手"}
            </div>
            <div className="home-step-label" style={{ fontSize: 15, fontWeight: 800, whiteSpace: "nowrap" }}>{s.label}</div>
            <div className="home-step-meta" style={{ fontSize: 11.5, color: "var(--ink-3)", fontWeight: 600, whiteSpace: "nowrap" }}>{s.meta}</div>
          </div>
          {!last && (
            <div className="home-step-bar" style={{ flex: 1, height: 2.5, background: done ? "var(--green-border)" : "var(--border)", borderRadius: 2, marginTop: 25 }} />
          )}
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, value, unit, accent, sub }: { label: string; value: string; unit?: string; accent?: string; sub?: string }) {
  return (
    <div className="home-stat" style={{ flex: 1, padding: "0 20px", borderLeft: "1px solid var(--border)" }}>
      <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 700 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginTop: 4, whiteSpace: "nowrap" }}>
        <span className="num" style={{ fontSize: 23, fontWeight: 800, color: accent || "var(--ink)", letterSpacing: "-.01em" }}>{value}</span>
        {unit && <span style={{ fontSize: 12.5, color: "var(--ink-3)", fontWeight: 700 }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function NavCard({
  icon, title, desc, cta, accent, disabled, onClick,
}: {
  icon: keyof typeof Ic; title: string; desc: string; cta: string;
  accent: { bg: string; fg: string }; disabled?: boolean; onClick?: () => void;
}) {
  const Icon = Ic[icon];
  const style: React.CSSProperties = {
    display: "block", padding: "17px 19px", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1,
  };
  const inner = (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 11, background: accent.bg, color: accent.fg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={22} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15.5, fontWeight: 800 }}>{title}</div>
          <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600 }}>{desc}</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: disabled ? "var(--ink-4)" : "var(--blue)" }}>{disabled ? "準備中" : cta}</span>
        {!disabled && <span style={{ color: "var(--blue)" }}><Ic.arrowR size={17} /></span>}
      </div>
    </>
  );
  return (
    <div className={"card" + (disabled ? "" : " nav-card")} style={style} onClick={disabled ? undefined : onClick}>
      {inner}
    </div>
  );
}

export function HomeScreen({ onNav }: { onNav: (s: string) => void }) {
  const { state } = useStore();

  // ===== store からの導出 =====
  const masterRegistered = state.drivers.length;
  const enrolled = state.drivers.filter((d) => !isRetiredDriver(d)).length;

  const anyImport = !!state.imports.yamato || !!state.imports.sagawa;
  const csvRows = (state.imports.yamato?.meta.rows || 0) + (state.imports.sagawa?.meta.rows || 0);
  const carrierLabels = [
    state.imports.yamato ? "ヤマト" : null,
    state.imports.sagawa ? "佐川" : null,
  ].filter(Boolean).join("・");

  const stmts = state.statements;
  const count = stmts.length;
  const netTotal = stmts.reduce((a, s) => a + s.netTotal, 0);
  const calcDone = count > 0;
  // 要確認 = （警告あり or 売上0：単価0・マスター未登録など）かつ 未確定（確定済みは除外）
  const needsAttention = stmts.filter((s) => (s.warnings.length > 0 || s.salesTotal <= 0) && s.status !== "confirmed").length;
  const confirmedCount = stmts.filter((s) => s.status === "confirmed").length;
  const allConfirmed = count > 0 && confirmedCount === count;

  // 承認状況（KV）。ローカル環境では取得不可 → null フォールバック
  const [approval, setApproval] = useState<{ total: number; approved: number; question: number; published: boolean } | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await fetchApprovalStatus(state.targetMonth);
      if (!alive) return;
      if (r.ok && r.items.length > 0) {
        setApproval({
          total: r.items.length,
          approved: r.items.filter((i) => i.status === "approved").length,
          question: r.items.filter((i) => i.status === "question").length,
          published: true,
        });
      } else {
        setApproval(null);
      }
    })();
    return () => { alive = false; };
  }, [state.targetMonth]);

  const published = approval?.published ?? false;

  // ===== 進捗ステップ =====
  const steps: Step[] = useMemo(() => {
    const importState: StepState = anyImport ? "done" : "current";
    const calcState: StepState = calcDone ? "done" : anyImport ? "current" : "todo";
    let checkState: StepState = "todo";
    let confirmState: StepState = "todo";
    let approveState: StepState = "todo";
    if (calcDone) {
      if (!allConfirmed) {
        checkState = "current";
      } else {
        checkState = "done";
        confirmState = published ? "done" : "current";
        approveState = published ? "current" : "todo";
      }
      if (allConfirmed && published) approveState = "current";
    }
    return [
      { key: "import", label: "CSV取込", icon: "upload", state: importState, meta: anyImport ? `${carrierLabels} ${csvRows.toLocaleString()}行` : "未取込" },
      { key: "calc", label: "自動計算", icon: "settings", state: calcState, meta: calcDone ? `${count}名 ・ 1人1通に統合` : "取込後に自動計算" },
      { key: "check", label: "確認・修正", icon: "list", state: checkState, meta: calcDone ? `要確認 ${needsAttention}件` : "—" },
      { key: "confirm", label: "確定", icon: "check", state: confirmState, meta: calcDone ? `${confirmedCount} / ${count}名` : "—" },
      { key: "approve", label: "承認", icon: "checkCircle", state: approveState, meta: published ? `${approval?.approved ?? 0} / ${approval?.total ?? 0}名` : "発行待ち" },
    ];
  }, [anyImport, calcDone, allConfirmed, published, carrierLabels, csvRows, count, needsAttention, confirmedCount, approval]);

  const doneCount = steps.filter((x) => x.state === "done").length;

  // ===== 次にやること =====
  const nextAction = (() => {
    if (!anyImport)
      return { title: "ヤマト・佐川のCSVを取り込む", body: <>まずは月次のCSVを取り込みます。自動判定でヤマト・佐川を振り分け、<b style={{ color: "#fff" }}>1人1通</b>の支払明細を計算します。</>, cta: "CSV取込を開始する", to: "csv" };
    if (!calcDone)
      return { title: "支払明細を計算する", body: <>CSVは取り込み済みです。マスターと突合して支払明細を計算します。</>, cta: "支払明細 一覧で確認する", to: "list" };
    if (needsAttention > 0)
      return { title: "明細を確認して、おかしい所を直す", body: <>計算は完了しています。<b style={{ color: "#fff" }}>要確認 {needsAttention}件</b>（単価0・マスター未登録）を一覧で確認してください。</>, cta: "支払明細 一覧で確認する", to: "list" };
    if (!allConfirmed)
      return { title: "明細を確定する", body: <>要確認はありません。内容を確認のうえ、各明細を確定してください（<b style={{ color: "#fff" }}>{confirmedCount} / {count}名</b> 確定済み）。</>, cta: "支払明細 一覧で確定する", to: "list" };
    if (!published)
      return { title: "ドライバーへ承認依頼を送る", body: <>全{count}名が確定済みです。各ドライバーへ承認依頼を発行できます。</>, cta: "支払明細 一覧へ", to: "list" };
    return { title: "承認状況を確認する", body: <>承認依頼を発行済みです。<b style={{ color: "#fff" }}>{approval?.approved ?? 0} / {approval?.total ?? 0}名</b> が承認済み{approval && approval.question > 0 ? `、質問 ${approval.question}件` : ""}。</>, cta: "承認状況を見る", to: "approval" };
  })();

  return (
    <div className="content" style={{ maxWidth: 1320 }}>
      <div className="page-head-home">
        <h1 className="page-title" style={{ fontSize: 24 }}>ホーム</h1>
        <div className="page-sub muted" style={{ marginTop: 5 }}>
          {formatMonth(state.targetMonth)} の処理状況 ／ 在籍 {enrolled} 名（マスター登録 {masterRegistered} 名）
        </div>
      </div>

      {/* 進捗ヒーロー */}
      <div className="card home-hero" style={{ padding: "22px 26px 24px", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>今月の進捗</div>
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", fontWeight: 700 }}>
            <span className="num" style={{ color: "var(--ink)" }}>{doneCount}</span> / {steps.length} ステップ完了
          </div>
        </div>
        <div className="home-steps" style={{ display: "flex" }}>
          {steps.map((st, i) => <ProgressStep key={st.key} s={st} last={i === steps.length - 1} />)}
        </div>
      </div>

      {/* 次にやること + 当月速報 */}
      <div className="home-2col" style={{ marginBottom: 18 }}>
        {/* 次にやること */}
        <div className="card" style={{ background: "var(--navy)", color: "#fff", padding: "22px 24px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: 12.5, color: "#aeb8cf", fontWeight: 800, letterSpacing: ".06em" }}>次にやること</div>
          <div style={{ fontSize: 21, fontWeight: 800, marginTop: 6, lineHeight: 1.35 }}>{nextAction.title}</div>
          <div style={{ fontSize: 13, color: "#aeb8cf", marginTop: 6 }}>{nextAction.body}</div>
          <button
            className="btn primary"
            onClick={() => onNav(nextAction.to)}
            style={{ alignSelf: "flex-start", marginTop: 16, height: 44, fontSize: 14.5, padding: "0 18px" }}
          >
            {nextAction.cta} <Ic.arrowR size={17} />
          </button>
        </div>

        {/* 当月速報 */}
        <div className="card" style={{ padding: "20px 4px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ padding: "0 20px 14px", fontSize: 13.5, fontWeight: 800, color: "var(--ink-2)" }}>当月の速報（{state.targetMonth}）</div>
          <div className="home-stats" style={{ display: "flex" }}>
            <StatMini label="対象明細" value={count.toLocaleString()} unit="件" sub="1人1通に統合済み" />
            <StatMini label="差引支給 合計" value={"¥" + yen(netTotal)} sub="確定後に振込データへ" />
            {approval ? (
              <StatMini label="承認済み" value={approval.approved + " / " + approval.total} unit="名" accent="var(--green)" sub={"質問 " + approval.question + "件"} />
            ) : (
              <StatMini label="確定済み" value={confirmedCount + " / " + count} unit="名" accent="var(--green)" sub={needsAttention > 0 ? `要確認 ${needsAttention}件` : "要確認なし"} />
            )}
          </div>
        </div>
      </div>

      {/* 各機能へ */}
      <div className="sec-label-home"><b>各機能へ</b><span>毎月この順で進めます</span></div>
      <div className="home-navcards">
        <NavCard icon="upload" title="CSV取込" desc="ヤマト・佐川の明細を取り込む" cta="取込画面へ" accent={{ bg: "var(--blue-soft)", fg: "var(--blue-dark)" }} onClick={() => onNav("csv")} />
        <NavCard icon="list" title="支払明細 一覧" desc="計算結果を確認・確定" cta="一覧を開く" accent={{ bg: "var(--blue-soft)", fg: "var(--blue-dark)" }} onClick={() => onNav("list")} />
        <NavCard icon="checkCircle" title="承認状況" desc="承認済・未読・質問ありを把握" cta="状況を見る" accent={{ bg: "var(--green-soft)", fg: "var(--green)" }} onClick={() => onNav("approval")} />
      </div>
    </div>
  );
}
